import { ApolloClient } from 'apollo-client';
import { ApolloLink } from 'apollo-link';
import { createHttpLink } from 'apollo-link-http';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { withClientState } from 'apollo-link-state';

import {
  BASE_API_URL,
  CREDENTIALS,
  GRAPH_ENDPOINT_SEARCH,
  GRAPH_ENDPOINT_B2B,
  GRAPH_ENDPOINT_PRIVATE,
  GRAPH_ENDPOINT_PAYMENTS,
  GRAPH_ENDPOINT_V3,
  GRAPH_ENDPOINT_V4,
  GRAPH_ENDPOINT_AUTH,
} from '../CONSTANTS';
import errorLink from './Links/errorLink';
import csrfTokenLink from './Links/csrfTokenLink';
import authConfig from './Links/authLink';
import currencyLink from './Links/currencyLink';
import appReleaseLink from './Links/appReleaseLink';
import distinctId from './Links/distinctId';
import crehanaCurrentUrlLink from './Links/crehanaCurrentUrlLink';

type CreateApolloClientOptions = {
  clientState?: any;
  uri?: string;
  credentials?: 'include' | 'same-origin';
};

const createApolloClient = ({
  clientState,
  uri,
  credentials,
}: CreateApolloClientOptions = {}) => {
  const common = { credentials: credentials || CREDENTIALS };

  const mainLink = createHttpLink({
    uri: uri || BASE_API_URL,
    ...common,
  });

  const privateLink = ApolloLink.split(
    operation => operation.getContext().clientName === 'private',
    createHttpLink({
      uri: GRAPH_ENDPOINT_PRIVATE,
      ...common,
    }),
    mainLink,
  );

  const authLink = ApolloLink.split(
    operation => operation.getContext().clientName === 'auth',
    createHttpLink({
      uri: GRAPH_ENDPOINT_AUTH,
      ...common,
    }),
    privateLink,
  );

  const v4Link = ApolloLink.split(
    operation => operation.getContext().clientName === 'v4',
    createHttpLink({
      uri: GRAPH_ENDPOINT_V4,
      ...common,
    }),
    authLink,
  );

  const v3Link = ApolloLink.split(
    operation => operation.getContext().clientName === 'v3',
    createHttpLink({
      uri: GRAPH_ENDPOINT_V3,
      ...common,
    }),
    v4Link,
  );

  const paymentsLink = ApolloLink.split(
    operation => operation.getContext().clientName === 'payments',
    createHttpLink({
      uri: GRAPH_ENDPOINT_PAYMENTS,
      ...common,
    }),
    v3Link,
  );

  const b2bLink = ApolloLink.split(
    operation => operation.getContext().clientName === 'b2b',
    createHttpLink({
      uri: GRAPH_ENDPOINT_B2B,
      ...common,
    }),
    paymentsLink,
  );

  const schemaLinks = ApolloLink.split(
    operation => operation.getContext().clientName === 'search',
    createHttpLink({
      uri: GRAPH_ENDPOINT_SEARCH,
      ...common,
    }),
    b2bLink,
  );

  const cache = new InMemoryCache();
  const links = [
    errorLink,
    csrfTokenLink,
    distinctId,
    crehanaCurrentUrlLink,
    authConfig,
    currencyLink,
    appReleaseLink,
    schemaLinks,
  ];
  let stateLink = null;

  if (clientState) {
    const { resolvers, defaults, typeDefs } = clientState;

    stateLink = withClientState({
      cache,
      resolvers,
      defaults,
      typeDefs,
    });
  }

  if (stateLink) {
    links.push(stateLink);
  }

  const client = new ApolloClient({
    cache,
    link: ApolloLink.from(links),
    connectToDevTools: process.env.NODE_ENV !== 'production',
  });

  return client;
};

export default createApolloClient;
